// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// Copyright (c) Utrecht University (Department of Information and Computing Sciences)

import axios from "axios";
import * as types from "@/utils/Types";
import * as utils from "@/utils/Functions";

/**
 * Gets all the bundles that are made by the current user (teacher or admin)
 * @returns List of bundles
 */
async function getMyBundlesTeacher(): Promise<types.bundle[] | null> {
  try {
    const res = await axios.get<types.bundle[]>("Bundle/MyCreatedBundles");
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets all bundles that can be added to a class (teacher or admin)
 * @returns List of bundles
 */
async function getAddableBundles(): Promise<types.bundle[] | null> {
  try {
    const res = await axios.get("Bundle/AddableBundles");
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets the settings for a level
 * @param bundleId The ID of the bundle of this level
 * @param levelId The ID of this level
 * @returns The settings for this particular level
 */
async function getLevelSettings(
  bundleId: number,
  levelId: number
): Promise<types.levelSettings | null> {
  try {
    if (bundleId) {
      const res = await axios.get<types.levelSettings>(
        `/Bundle/LevelSettings/${bundleId}/${levelId}`
      );
      utils.storeLevelSettings(res.data);
      return res.data;
    } else {
      return null;
    }
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets a list of all levels in a bundle
 * @param bundleId The ID of the bundle
 * @returns A list of levels
 */
async function getAllLevelsInBundle(
  bundleId: number
): Promise<types.level[] | null> {
  try {
    const res = await axios.get<types.level[]>(`/Bundle/Levels/${bundleId}`);
    utils.storeLevels(res.data);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets the settings of all levels in a bundle
 * @param bundleId The ID of the bundle
 * @returns A list of levels with their settings
 */
async function getBundleLevelSettings(
  bundleId: number
): Promise<types.bundleLevelSettings[] | null> {
  try {
    const res = await axios.get<types.bundleLevelSettings[]>(
      `/Bundle/GetBundleLevelSettings/${bundleId}`
    );
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Creates a new bundle
 * @param bundleName The name of the new bundle you want to create
 * @returns The created bundle
 */
async function createBundle(bundleName: string): Promise<types.bundle | null> {
  try {
    const res = await axios.post(`/Bundle/Create/${bundleName}`);
    return res.data as types.bundle;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Creates a copy of a bundle and returns the new bundleId
 * @param bundleId The ID of the bundle you want to make a copy of
 * @returns The bundle
 */
async function cloneBundle(bundleId: number): Promise<types.bundle | null> {
  try {
    const res = await axios.post<types.bundle>(`/Bundle/Clone/${bundleId}`);
    return res.data as types.bundle;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Delete a bundle permanently
 * @param bundleId The ID of the bundle you want to delete
 * @returns A HTTP status code
 */
async function deleteBundle(bundleId: number): Promise<number | null> {
  try {
    const res = await axios.delete(`/Bundle/Delete/${bundleId}`);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Requests that the bundle be published to Stichting Praktijkleren
 * and receives the mail information to go into a mailto
 * @param bundleId The ID of the bundle you want to publish
 * @returns A mailTo
 */
async function requestPublicationBundle(
  bundleId: number
): Promise<string | null> {
  try {
    const res = await axios.get<string>(
      `/Bundle/RequestPublication/${bundleId}`
    );
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Get a list of all pubished bundles
 * @returns A list of all pubished bundles
 */
async function getPublishedBundles(): Promise<types.bundle[] | null> {
  try {
    const res = await axios.get<types.bundle[]>(`/Bundle/Published/`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Update information about a bundle
 * @param bundle The newly updated information of the bundle
 * @returns A HTTP status code
 */
async function updateBundle(bundle: types.bundle): Promise<number | null> {
  try {
    const res = await axios.put(`/Bundle/Update`, bundle);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Publishes a bundle to be available for classrooms
 * @param bundleId The ID of the bundle you want to publish
 * @returns A HTTP status code
 */
async function publishBundle(bundleId: number): Promise<number | null> {
  try {
    const res = await axios.put(`/Bundle/Publish/${bundleId}`);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Update the settings of a level in a bundle
 * @param bundleSettings The new settings
 * @returns A HTTP status code
 */
async function updateBundleLevelSettings(
  bundleSettings: types.bundleLevelSettings
): Promise<number | null> {
  try {
    const res = await axios.put(`/Bundle/UpdateSettings`, bundleSettings);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Update the order of levels in a bundle
 * @param bundleId The ID of the bundle you want to update
 * @param levelOrder A list of ID's of a level and their new index
 * @returns A HTTP status code
 */
async function updateBundleLevelOrder(
  bundleId: number,
  levelOrder: { levelId: number; index: number }[]
): Promise<number | null> {
  try {
    const res = await axios.put(`/Bundle/UpdateOrder/${bundleId}`, levelOrder);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets information about a level
 * Usable with student permission
 * @param levelId The ID of the level
 * @param bundleId The bundle this level is in
 * @returns A level.
 */
async function getLevel(
  bundleId: number,
  levelId: number
): Promise<types.levelInfo | null> {
  try {
    if (bundleId) {
      const res = await axios.get<types.levelInfo>(
        `/Bundle/Level/${bundleId}/${levelId}`
      );
      utils.storeLevelInfo(res.data);
      return res.data;
    } else {
      return null;
    }
  } catch (err) {
    console.error(err);
    return null;
  }
}

export {
  getMyBundlesTeacher,
  getAddableBundles,
  getLevelSettings,
  getAllLevelsInBundle,
  getBundleLevelSettings,
  createBundle,
  cloneBundle,
  deleteBundle,
  requestPublicationBundle,
  getPublishedBundles,
  updateBundle,
  publishBundle,
  updateBundleLevelSettings,
  updateBundleLevelOrder,
  getLevel,
};
